import React, { useEffect, useState, useRef } from "react";
import { Box, Text, useThemeUI } from "theme-ui";
import { useSelector, useDispatch } from "react-redux";
import get from "lodash/fp/get";
import isEqual from "lodash/fp/isEqual";
import flow from "lodash/fp/flow";
import isString from "lodash/fp/isString";
import map from "lodash/fp/map";
import update from "lodash/fp/update";
import filter from "lodash/fp/filter";

import {
  selectUserChoicesForUi,
  selectSelectedProduct,
  selectAddToOrderValues,
  selectUserChoiceValues,
  selectProductHeaderProps,
  selectProductImages,
} from "store/app";
import { displayShareModal, clearLoadedRecipe } from "store/app/appSlice";
import { setCustomisationStage } from "store/app/appSlice";

import * as styles from "./ReviewOrderPage.styles";
import { Button, ProductImage, SvgOrImg, LoadingIndicator } from "components";
import {
  ArrowRightIcon,
  CopyIcon,
  DownloadIcon,
  MapPinIcon,
  PrintIcon,
  ShareIcon,
} from "components/icons";
import DownloadSpecsButton from "./DownloadSpecsButton";
import { useTranslation } from "hooks";
import { createRecipe } from "api";

export default function ReviewOrderPage({
  appDivider,
  readyToOrder,
  setReadyToOrder,
  setShowReviewOrderPage,
  appHeaderLogo,
  publicDirectoryUrl,
}) {
  const { t } = useTranslation();

  const { loadedRecipeID, webAppValues } = useSelector((state) => {
    return state?.app?.loadedRecipe;
  });

  const [isLoading, setIsLoading] = useState(false);

  const [recipeID, setRecipeId] = useState(loadedRecipeID);

  const {
    theme: { isMobile, isDesktop },
  } = useThemeUI();

  const dispatchDisplayShareModal = () => dispatch(displayShareModal());

  const dispatchClearLoadedRecipe = () => dispatch(clearLoadedRecipe());

  const realUserChoiceValues = useSelector(selectUserChoiceValues);

  const { logo, pdfHeaderColour } = useSelector(selectProductHeaderProps);

  const productImages = useSelector(selectProductImages);
  const images = productImages.map(
    update("images", flow(map(get("url")), filter(isString)))
  );

  //* Note: userChoiceValues are FORMATTED for recipe Endpoint (not the same as userChoiceValues in Redux Store)
  const userChoiceValues = useSelector(selectAddToOrderValues);
  const userChoicesForUiV2 = useSelector(selectUserChoicesForUi(t));

  const { name: productName } = useSelector(selectSelectedProduct);

  const dispatch = useDispatch();

  const dispatchSetCustomisationStage = (stage) => {
    dispatch(setCustomisationStage(stage));
  };

  const parentContainerRef = useRef();
  const reviewOverviewContainerRef = useRef();

  const handleClickToFinalStep = () => {
    const topLeftAuto = {
      top: 0,
      left: 0,
      behavior: "auto",
    };
    if (parentContainerRef.current) {
      parentContainerRef.current.scrollTo(topLeftAuto);
    }
    if (reviewOverviewContainerRef.current) {
      reviewOverviewContainerRef.current.scrollTo(topLeftAuto);
    }
    setReadyToOrder(true);
    dispatchSetCustomisationStage(4);
  };

  const handleClickToMakeChanges = () => {
    setShowReviewOrderPage(false);
    dispatchSetCustomisationStage(2);
  };

  const useSameRecipeId = isEqual(webAppValues, realUserChoiceValues);

  useEffect(() => {
    if (useSameRecipeId) return;
    const controller = new AbortController();

    setIsLoading(true);
    createRecipe({
      signal: controller.signal,
      images,
    })({ userChoiceValues })
      .then(get(["Item", "recipeID", "S"]))
      .then(setRecipeId)
      .then(() => {
        setIsLoading(false);
        dispatchClearLoadedRecipe();
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [useSameRecipeId]);

  const uiText = useSelector(get(["app", "formData", "uiText"]));
  const {
    reviewPageUiText: {
      reviewProduct,
      reviewCopy1a,
      reviewCopy1b,
      reviewCopy2a,
      reviewCopy2b,
    },
    link_1,
  } = uiText;

  const Panel1 = () => (
    <Box sx={styles.reviewInfoPanelContent}>
      <Box sx={styles.reviewOrderRecipe}>
        <Text variant="h4Bold" sx={{ lineHeight: "30px" }}>
          {t("Recipe ID: ")}
          {!isLoading && (
            <Text variant="h4" as="span">
              {recipeID}
            </Text>
          )}
        </Text>

        {isLoading && <LoadingIndicator sx={styles.recipeLoadingIndicator} />}

        {!isLoading && recipeID && (
          <Button
            variant="secondary"
            size="small"
            isRound
            iconAfter={<CopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(recipeID);
            }}
          >
            {t("Copy")}
          </Button>
        )}
      </Box>
      <Text
        as="div"
        variant={isMobile ? "copy3" : "copy4"}
        mt="20px"
        sx={styles.reviewCopy}
      >
        {reviewCopy1a}
      </Text>
      <Text
        as="div"
        mt="10px"
        variant={isMobile ? "copy3" : "copy4"}
        sx={styles.reviewCopy}
        dangerouslySetInnerHTML={{
          __html: t(reviewCopy1b, { link_1 }),
        }}
      />
      {/* DEV NOTE: Appears in Panel 3 also (see further down) */}
      <Box sx={styles.reviewActions}>
        <DownloadSpecsButton
          userChoicesForUiV2={userChoicesForUiV2}
          userChoiceValues={userChoiceValues}
          headerBackgroundColour={pdfHeaderColour}
          appHeaderLogo={appHeaderLogo}
          productLogo={logo}
          recipeID={recipeID}
          iconBefore={<PrintIcon width={"25px"} height={"25px"} />}
          reviewProduct={reviewProduct}
          productName={productName}
          images={images}
          publicDirectoryUrl={publicDirectoryUrl}
          sx={styles.reviewAction}
        >
          {t("Print Specs")}
        </DownloadSpecsButton>

        <DownloadSpecsButton
          userChoicesForUiV2={userChoicesForUiV2}
          userChoiceValues={userChoiceValues}
          headerBackgroundColour={pdfHeaderColour}
          appHeaderLogo={appHeaderLogo}
          productLogo={logo}
          recipeID={recipeID}
          iconBefore={<DownloadIcon width={"25px"} height={"25px"} />}
          reviewProduct={reviewProduct}
          productName={productName}
          images={images}
          publicDirectoryUrl={publicDirectoryUrl}
          sx={styles.reviewAction}
        >
          {t("Download Specs")}
        </DownloadSpecsButton>

        <Button
          iconBefore={<ShareIcon width={"25px"} height={"25px"} />}
          variant="unstyled"
          sx={styles.reviewAction}
          onClick={dispatchDisplayShareModal}
        >
          {t("Share your custom design")}
        </Button>
      </Box>

      <Button
        iconAfter={<ArrowRightIcon />}
        variant="primary"
        size={isMobile ? "medium" : "large"}
        isRound
        sx={{ width: "100%" }}
        onClick={handleClickToFinalStep}
      >
        {t("Looks good! How do I order?")}
      </Button>
    </Box>
  );

  const Panel2 = ({ isStatic = false }) => (
    <Box sx={styles.reviewOverviewPanelContent}>
      <Text variant="h4">
        {`${
          !isStatic && !readyToOrder ? t("Let’s Review Your") : t("Your")
        } ${productName} ${reviewProduct}`}
      </Text>

      {!isStatic && !readyToOrder && (
        <Button
          variant="secondary"
          size="medium"
          isRound
          sx={{ marginTop: "20px" }}
          onClick={handleClickToMakeChanges}
        >
          {t("Make changes?")}
        </Button>
      )}

      <Box sx={styles.reviewOverviewImageContainer}>
        <ProductImage withPinchZoom={false} isReviewOrderPage />
        {!isStatic && readyToOrder && (
          <SvgOrImg
            src={
              "https://cdn.media.amplience.net/i/bauer/Bauer-Approved-Badge.webp"
            }
            alt={t("Bauer. Approved for you!")}
            sx={styles.approvedStamp}
          />
        )}
      </Box>

      <Box sx={styles.reviewOverviewSpecs}>
        {userChoicesForUiV2.map(({ title, values }) => {
          return (
            <Box sx={styles.specCustomisation} key={title}>
              <Text as="div" variant="copy3" sx={styles.specCustomisationName}>
                {title}
              </Text>

              {values.map((valueOrValues, index) => {
                return (
                  <Box sx={styles.specsOptionsValues} key={index}>
                    <Box sx={styles.specsOptionValue}>
                      <Text variant="copy1" sx={{ fontFamily: "dinCondensed" }}>
                        {valueOrValues.label && `${valueOrValues.label}: `}
                      </Text>
                      <Text
                        variant="copy1"
                        sx={{
                          fontWeight: 700,
                          fontFamily: "dinCondensed",
                        }}
                      >
                        {!Array.isArray(valueOrValues.value)
                          ? valueOrValues.value
                          : valueOrValues.value.map(({ label, value }) => {
                              return (
                                <Box
                                  sx={styles.specsSubOptionValue}
                                  key={label}
                                >
                                  <Text
                                    variant="copy2Light"
                                    sx={{
                                      fontFamily: "dinCondensed",
                                    }}
                                  >
                                    {label}
                                    {value && ": "}
                                  </Text>
                                  <Text
                                    variant="copy2"
                                    sx={{
                                      fontFamily: "dinCondensed",
                                    }}
                                  >
                                    {value}
                                  </Text>
                                </Box>
                              );
                            })}
                      </Text>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const Panel3 = () => (
    <Box sx={styles.reviewFinalPanelContent}>
      <Text variant="h1" sx={{ lineHeight: "60px" }}>
        {t("You're ready to order")}
      </Text>
      <Box sx={styles.reviewOrderRecipe} mt={"30px"}>
        <Text variant="h4Bold" sx={{ lineHeight: "30px" }}>
          {t("Recipe ID: ")}
          {!isLoading && (
            <Text variant="h4" as="span">
              {useSameRecipeId ? loadedRecipeID : recipeID}
            </Text>
          )}
        </Text>

        {isLoading && <LoadingIndicator sx={styles.recipeLoadingIndicator} />}
        <Button
          variant="secondary"
          size="small"
          isRound
          iconAfter={<CopyIcon />}
          onClick={() => {
            navigator.clipboard.writeText(recipeID);
          }}
        >
          {t("Copy")}
        </Button>
      </Box>
      <Text
        as="div"
        variant="copy1"
        mt="30px"
        sx={{
          a: {
            color: "primary",
          },
        }}
        dangerouslySetInnerHTML={{
          __html: t(reviewCopy2a, { link_1 }),
        }}
      />

      <Text as="div" variant="copy1" mt="10px">
        {reviewCopy2b}
      </Text>

      <Button
        iconBefore={<MapPinIcon />}
        variant="primary"
        size={isMobile ? "medium" : "large"}
        isRound
        sx={{ marginTop: "30px", width: "100%" }}
        // onClick={}
      >
        {t("Order at a retailer near you")}
      </Button>

      {/* DEV NOTE: Appears in Panel 1 also (see higher up) */}
      <Box sx={styles.reviewActions}>
        <DownloadSpecsButton
          userChoicesForUiV2={userChoicesForUiV2}
          userChoiceValues={userChoiceValues}
          headerBackgroundColour={pdfHeaderColour}
          appHeaderLogo={appHeaderLogo}
          productLogo={logo}
          recipeID={recipeID}
          iconBefore={<PrintIcon width={"25px"} height={"25px"} />}
          reviewProduct={reviewProduct}
          productName={productName}
          images={images}
          publicDirectoryUrl={publicDirectoryUrl}
          sx={styles.reviewAction}
        >
          {t("Print Specs")}
        </DownloadSpecsButton>

        <DownloadSpecsButton
          userChoicesForUiV2={userChoicesForUiV2}
          userChoiceValues={userChoiceValues}
          headerBackgroundColour={pdfHeaderColour}
          appHeaderLogo={appHeaderLogo}
          productLogo={logo}
          recipeID={recipeID}
          iconBefore={<DownloadIcon width={"25px"} height={"25px"} />}
          reviewProduct={reviewProduct}
          productName={productName}
          images={images}
          publicDirectoryUrl={publicDirectoryUrl}
          sx={styles.reviewAction}
        >
          {t("Download Specs")}
        </DownloadSpecsButton>

        <Button
          iconBefore={<ShareIcon width={"25px"} height={"25px"} />}
          variant="unstyled"
          sx={styles.reviewAction}
          onClick={dispatchDisplayShareModal}
        >
          {t("Share your custom design")}
        </Button>
      </Box>
    </Box>
  );

  if (isDesktop) {
    return (
      <Box sx={styles.reviewOrderPage}>
        <Box ref={parentContainerRef} sx={styles.reviewOrderContainer}>
          <Box sx={styles.reviewOrderPanels({ readyToOrder })}>
            <Box sx={styles.reviewInfoPanel}>
              <Panel1 />
            </Box>

            <Box
              ref={reviewOverviewContainerRef}
              sx={styles.reviewOverviewPanel}
            >
              <Panel2 />
            </Box>

            <Box sx={styles.reviewFinalPanel}>
              <Panel3 />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.reviewOrderPage}>
      <Box ref={parentContainerRef} sx={styles.reviewOrderContainer}>
        <Box sx={styles.reviewOrderPanels({ readyToOrder })}>
          <Box sx={styles.reviewInfoPanel}>
            <Panel2 />

            <SvgOrImg
              src={appDivider.url}
              alt={appDivider.alt}
              sx={styles.mobileDivider}
            />

            <Panel1 />
          </Box>

          <Box sx={styles.reviewFinalPanel}>
            <Panel3 />

            <SvgOrImg
              src={appDivider.url}
              alt={appDivider.alt}
              sx={styles.mobileDivider}
            />

            <Panel2 isStatic />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
